"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useRouter } from "next/navigation"
import { motion } from "framer-motion"
import { Star } from "lucide-react"
import { handleSwissCTAClick, handleDefaultCTAClick } from "../utils/cta"
import { trackEvent } from "../lib/analytics"

const Testimonials = ({ isSwiss = false }) => {
  const { t } = useTranslation("Testimonials")
  const reviews = t("reviews", { returnObjects: true })
  const router = useRouter()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleCTAClick = () => {
    trackEvent("cta_click", { location: "testimonials" })
    if (isSwiss) {
      handleSwissCTAClick(router)
    } else {
      handleDefaultCTAClick(router)
    }
  }

  if (!mounted) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="h-64 animate-pulse bg-white rounded-xl" />
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">
            {t("sectionLabel")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            {t("title")}
          </h2>
          <p className="text-slate-600 mt-2">
            {t("subtitle")}
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {Array.isArray(reviews) && reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                &ldquo;{review.text}&rdquo;
              </p>
              <div className="flex items-center gap-2.5 pt-3 border-t border-slate-100">
                <span className="text-xl">{review.flag}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{review.name}</p>
                  <p className="text-xs text-slate-500">{review.language}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={handleCTAClick}
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3 rounded-full transition duration-200 hover:shadow-lg hover:shadow-blue-600/25"
          >
            {t(isSwiss ? "ctaButtonSwiss" : "ctaButton")}
          </button>
        </div>
      </div>
    </section>
  )
}

export default Testimonials
